export type Language = 'en' | 'id'

export const translations = {
  en: {
    // Navigation
    home: 'Home',
    about: 'About',
    projects: 'Projects',
    blog: 'Blog',
    contact: 'Contact',
    donate: 'Donate',
    volunteer: 'Volunteer',
    members: 'Members',
    partner: 'Partner',
    impact: 'Impact',
    help: 'Help',

    // Common
    learnMore: 'Learn More',
    readMore: 'Read More',
    getStarted: 'Get Started',
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    loading: 'Loading...',

    // Hero Section
    heroTitle1: 'Every Child Deserves to Learn at Their Own Pace',
    heroTitle2: 'Building Strong Learning Foundations for Indonesia’s Future Generation',
    heroTitle3: 'Helping Children Discover Confidence Through Learning',
    heroSubtitle1:
      'Together, we help children in Indonesia read, count, and grow in confidence through the Teaching at the Right Level (TaRL) approach — so no child is left behind.',
    heroSubtitle2:
      'We support children in remote communities by ensuring access to quality foundational learning.',
    heroSubtitle3:
      'Your support accelerates real progress in children’s reading, numeracy, and confidence—especially in underserved areas.',
    donateNow: 'Donate Now',
    makeImpactToday: 'Make an impact today',
    viewPrograms: 'Explore Our Programs',
    seeWhereWeWork: 'See where we work',
    scrollToExplore: 'Scroll to explore',

    // Impact Stats
    ourImpact: 'Our Impact',
    makingRealDifference: 'Making a Real Difference',
    impactStatsDesc:
      'Real change happens when communities come together. Here’s how your support is helping children learn and grow.',
    booksDistributed: 'Books Distributed',
    booksDistributedDesc: 'Providing engaging, level-based reading materials for every child.',
    teachersTrained: 'Teachers Trained',
    teachersTrainedDesc:
      'Empowering educators with the Teaching at the Right Level (TaRL) approach.',
    schoolsSupported: 'Schools Supported',
    schoolsSupportedDesc:
      'Improving learning environments through materials, resources, and guidance.',
    regionsServed: 'Region Served',
    regionsServedDesc: 'Starting meaningful educational efforts through local partnerships.',
    totalFunding: 'Total Funding Raised',
    totalFundingDesc:
      'Thanks to generous donors and partners who believe in equal learning opportunities.',
    totalFundingAmount: 'Rp 285M',
    annualGoalProgress: '2026 Impact Goal Progress',
    annualGoalProgressDesc: 'We aim to reach 5,000 students by 2026.',
    goalProgressStatus:
      'So far, 216 students have been supported — a strong start toward our mission.',
    studentsReached: 'Students Reached',

    // How It Works
    simpleTransparent: 'Simple & Transparent',
    howDonationCreatesChange: 'How Your Donation Creates Change',
    howDonationDesc:
      'From your generosity to real student success — every step is tracked and shared with you.',
    chooseYourImpact: 'Choose Your Impact',
    chooseYourImpactDesc: 'Browse featured schools and programs that need your support today.',
    chooseDetail1: 'View school profiles',
    chooseDetail2: 'See what your donation supports',
    chooseDetail3: 'Track real-time progress',
    donateSecurely: 'Donate Securely',
    donateSecurelyDesc: 'Give once or monthly — safely through QRIS or credit card.',
    donateDetail1: '100% secure platform',
    donateDetail2: 'Add a personal message',
    trackProgress: 'Track Progress',
    trackProgressDesc: 'Stay connected with transparent updates from the field.',
    trackDetail1: 'Monthly impact reports',
    trackDetail2: 'Photos and stories from students',
    trackDetail3: 'Ongoing progress tracking',
    seeResults: 'See the Results',
    seeResultsDesc: 'Watch how your support helps students learn, grow, and thrive.',
    seeDetail1: 'Measurable outcomes',
    seeDetail2: 'Before-and-after results',
    seeDetail3: 'Community-wide impact',
    readyToMakeDifference2: 'Ready to Make a Difference?',
    readyToMakeDifferenceDesc2:
      'Join a growing community of changemakers transforming education in Indonesia.',
    startDonating: 'Start Donating',
    startDonatingDesc: 'Empower schools and students today.',
    becomeVolunteer: 'Become a Volunteer',
    becomeVolunteerDesc: 'Share your time, skills, and heart.',

    // Volunteer CTA
    volunteerCtaDesc:
      'Together, we can strengthen the foundation of education across Indonesia. Whether you’re an individual, a university, or an organization, your involvement creates meaningful impact.',
    closingCtaTitle: 'Be Part of the Change',
    fieldVolunteerTitle: 'Volunteer on the Field',
    fieldVolunteerDesc:
      'Join our team of dedicated volunteers who work directly with schools and communities to improve literacy and numeracy learning.',
    academicRoles: 'Academic Roles',
    academicRolesDesc:
      'Teach literacy, numeracy, and provide additional support for students who need it.',
    nonAcademicRoles: 'Non-Academic Roles',
    nonAcademicRolesDesc: 'Assist in program operations, teacher training, or documentation.',
    whoCanJoin: 'Who Can Join',
    whoCanJoinDesc: 'Individuals or university partners ready to engage directly in the field.',
    implementationDesc:
      'Programs launch with full support from the Accelero field and operations team.',
    applyToVolunteer: 'Apply as Volunteer',

    // Partnership
    partnerWithUs: 'Partner with Us',
    createLastingImpact: 'Create Lasting Impact',
    getInvolved: 'Get Involved',
    partnershipDesc:
      'Collaborate with Accelero Indonesia through CSR or institutional partnerships to improve school facilities, provide level-appropriate learning materials, and ensure every student receives equal learning opportunities.',
    partnershipDesc2:
      'Your partnership helps us create fairer and more inclusive education for children across Indonesia.',
    strategicAlignment: 'Strategic Alignment',
    strategicAlignmentDesc: 'Partnerships tailored to your CSR goals',
    globalReach: 'Global Reach',
    globalReachDesc: 'Programs across multiple communities',
    learnAboutUs: 'Learn About Us',
    partnershipImpact: 'Partnership Impact',
    buildingStrongerCommunities: 'Building stronger communities together',
    activePartners: 'Active Partners',
    jointFunding: 'Joint Funding',
    livesImpacted: 'Lives Impacted',
    ngoPartnership: 'NGO / Community Partnership',
    ngoPartnershipDesc: 'Resource sharing programs',
    available: 'Available',
    trustedBy: 'Trusted By',

    // Partnership Page
    partnershipHeroTitle: 'Partner With Accelero',
    partnershipHeroSubtitle:
      'Together, we can expand access to quality education for thousands of students across Indonesia.',
    becomeAPartner: 'Become a Partner',
    partnerHeroCta: 'Become a Partner',

    // Why Partner
    whyPartner: 'Why Partner With Accelero?',
    meaningfulImpact: 'Meaningful & Measurable Impact',
    meaningfulImpactDesc:
      'Support proven programs that improve literacy, numeracy, and learning environments in underserved schools.',
    transparentReporting: 'Transparent Reporting',
    transparentReportingDesc:
      'Receive comprehensive reports with photos, data, progress metrics, and final outcomes.',
    accessToSchools: 'Access to School Communities',
    accessToSchoolsDesc:
      'Our programs operate across multiple regions, reaching schools with the greatest needs.',
    brandVisibility: 'Brand Visibility & CSR Strengthening',
    brandVisibilityDesc:
      'Enhance your corporate social responsibility profile through strategic, impactful collaboration.',

    // Partnership Opportunities
    partnershipOpportunities: 'Partnership Opportunities',

    // Corporate Partnership
    corporatePartnership: 'Corporate Partnership (CSR / Employee Engagement)',

    // University Partnership
    universityPartnership: 'University Partnership',
    universityPartnershipDesc:
      'Ideal for institutions implementing community service, internships, and research collaborations.',
    universityFeature1: 'Community service programs',
    universityFeature2: 'On-field student volunteering',
    universityFeature3: 'Education-focused research collaboration',
    universityFeature4: 'Scholarship support for teachers or schools',

    // Foundation Partnership
    foundationPartnership: 'Foundation Partnership',
    foundationPartnershipDesc: 'Work with us to scale your grant-making impact.',
    foundationFeature1: 'Co-funding educational programs',
    foundationFeature2: 'Program design & development',
    foundationFeature3: 'Long-term impact measurement',
    foundationFeature4: 'Regional expansion',

    // NGO Partnership
    ngoFeature1: 'Joint programs',
    ngoFeature2: 'Resource and expertise sharing',
    ngoFeature3: 'Community mobilization',
    ngoFeature4: 'Equitable access initiatives',

    // Partnership Success Stories
    partnershipSuccessStories: 'Partnership Success Stories',
    dPrizeStoryDesc:
      'D-Prize awarded Accelero funding and technical support to implement the Teaching at the Right Level (TaRL) approach through After School Programs in rural primary schools—starting in Sorong, Southwest Papua, one of Indonesia’s lowest-literacy regions.',
    dPrizeQuote:
      '“Congratulations to D-Prize winner, Accelero Indonesia! Led by Catherine Tikara and Kevin Evannanda Septian, Accelero delivers the Teaching at the Right Level (TaRL) approach through after-school programs in rural primary schools… Through their partnership with local universities, they recruit and train student volunteers, providing them with hands-on teaching experience while empowering them to serve their communities. At the end of its pilot, Accelero would have reached 200+ learners.”',
    dPrizeAttribution: '— D-Prize',

    // How Partnership Works
    howPartnershipWorks: 'How Partnership Works',
    initialConsultation: 'Initial Consultation',
    initialConsultationDesc: 'We explore your goals, values, and desired areas of impact.',
    customizedProposal: 'Customized Proposal',
    customizedProposalDesc:
      'You receive a tailored proposal outlining objectives, activities, timeline, and budget.',
    implementation: 'Implementation',
    impactReporting: 'Impact Reporting',
    impactReportingDesc: 'You receive transparent, data-driven updates at every stage.',

    // CTA
    readyToCreateImpact: 'Ready to Create Impact Together?',
    readyToCreateImpactDesc:
      "Let's explore how we can collaborate to build lasting educational change.",
    startConversation: 'Start a Conversation',

    // Partnership Types
    corporatePartnershipFull:
      'For companies seeking to invest in education and community development.',
    corporateFeature1: 'Literacy & numeracy programs',
    corporateFeature2: 'Library and learning space revitalization',
    corporateFeature3: 'On-field employee volunteering',
    corporateFeature4: 'Matching gift campaigns & recurring giving',
    foundationPartnershipFull: 'Work with us to scale your grant-making impact.',
    ngoPartnershipFull: 'Collaborate with other nonprofits to amplify collective impact.',

    // CTA
    supportSchool: 'Support schools that need better spaces to read, learn, and grow.',
    featuredPrograms: 'Featured Programs',
    ourPrograms: 'Our Programs',
    viewAllPrograms: 'View All Programs',
    supportThisProject: 'Support This Project',
    featuredProgramCta: 'Support This Project',
    viewImpact: 'View Impact',
    // Projects Page
    makeADifferenceToday: 'Make a Difference Today',
    browseOurPrograms:
      "Explore our programs and help partner schools build learning environments that strengthen children's foundational skills. Every donation is transparently recorded—and directly supports better learning spaces, relevant books, and facilities that nurture a strong reading culture.",
    activePrograms: 'Active Programs',
    totalRaised: 'Total Raised',
    searchPrograms: 'Search programs...',
    allPrograms: 'All Programs',
    literacy: 'Literacy',
    numeracy: 'Numeracy',
    teacherTraining: 'Teacher Training',
    infrastructure: 'Infrastructure',
    allStatus: 'All Status',
    active: 'Active',
    completed: 'Completed',
    newestFirst: 'Newest First',
    lowestFunded: 'Lowest Funded',
    highestFunded: 'Highest Funded',
    mostImpact: 'Most Impact',
    clear: 'Clear',
    results: 'results',
    result: 'result',
    noProgramsFound: 'No Programs Found',
    noProgramsFoundDesc:
      "We couldn't find any programs matching your criteria. Try adjusting your filters or search query.",
    noActiveProgramsDesc: "We don't have any active programs at the moment. Check back soon!",
    resetFilters: 'Reset Filters',
    cantFindWhatYoureLookingFor: "Can't Find What You're Looking For?",
    donateToGeneralFundDesc:
      "Make a general donation to our fund and we'll allocate it to the programs that need it most.",
    donateToGeneralFund: 'Donate to General Fund',
    contactUs: 'Contact Us',
    wantToMakeEvenBiggerImpact: 'Want to Make an Even Bigger Impact?',
    supportGeneralFundDesc:
      'Support our General Education Fund to help expand the TaRL After School Program across more partner schools.',

    // Project Detail Page
    aboutThisProgram: 'About This Program',
    theChallenge: 'The Challenge',
    whyThisMatters: 'Why This Matters',
    ourApproachProject: 'Our Approach',
    expectedImpact: 'Expected Impact',
    aboutTheSchool: 'About the School',
    overview: 'Overview',
    updates: 'Updates',
    budgetBreakdown: 'Budget Breakdown',
    timeline: 'Timeline',
    totalBudget: 'Total Budget',
    ofTotalBudget: 'of total budget',
    noUpdatesYet: 'No Updates Yet',
    noUpdatesDesc: 'Check back soon for progress updates on this program',
    budgetComingSoon: 'Budget Details Coming Soon',
    budgetComingSoonDesc: 'Detailed budget breakdown will be available shortly',
    timelineComingSoon: 'Timeline Coming Soon',
    timelineComingSoonDesc: 'Program milestones will be added as the project progresses',
    raised: 'Raised',
    goal: 'Goal',
    funded: 'funded',
    stillNeeded: 'still needed',
    donors: 'Donors',
    volunteers: 'Volunteers',
    shareThisProject: 'Share This Project',
    projectCompleted: 'Project Completed',
    secureTransparentDonations: 'Secure & transparent donations',
    yourImpact: 'Your Impact',
    seeHowDonationHelps: 'See how your donation helps:',
    providesReadingBooks: 'Provides 10 reading books',
    supportsStudentsMonth: 'Supports 5 students for 1 month',
    trainsTeacher: 'Trains 1 teacher in TaRL method',
    otherPrograms: 'Other Programs You Might Like',
    principal: 'Principal',
    totalStudents: 'Total Students',
    established: 'Established',
    studentsWillBenefit: 'students will benefit',
    toGo: 'to go',
    cardRaised: 'raised',
    cardOf: 'of',
    cardGoal: 'goal',

    // Impact Page
    measuringOurImpact: 'Our Impact',
    impactPageDesc:
      'In the early phase of implementation, 216 students have already benefited from Accelero’s learning support—and the number continues to grow.',
    impactByNumbers: 'Numbers That Tell the Story',
    impactStudentsLabel: 'Students Reached',
    impactStudentsDesc: 'Receiving literacy and numeracy support through the TaRL approach.',
    impactBooksLabel: 'Books Distributed',
    impactBooksDesc:
      'Providing engaging, level-appropriate reading materials for schools without libraries.',
    impactTeachersLabel: 'Teachers Trained',
    impactTeachersDesc:
      'Equipped with the TaRL methodology to strengthen classroom learning quality.',
    impactSchoolsLabel: 'Schools Supported',
    impactSchoolsDesc: 'Receiving improved learning facilities and after-school learning programs.',
    impactRegionLabel: 'Region Reached',
    impactRegionDesc:
      'Launching community-driven education initiatives in Sorong, Southwest Papua.',
    impactFundsAmount: 'IDR 285M',
    impactFundsLabel: 'Funds Raised',
    impactFundsDesc: 'Directly invested into improving learning outcomes for children.',

    // Impact Focus Areas
    whereWeCreateImpact: 'Where We Create Impact',
    focusAreasDesc: 'Focusing on the most urgent areas of educational development',
    literacyDevelopment: 'Literacy Development',
    literacyDevelopmentDesc: 'Helping students build reading foundations through TaRL',
    literacyStudentsCount: '850+ students showing reading improvement',
    numeracySkills: 'Numeracy Skills',
    numeracySkillsDesc: 'Supporting early math skills through interactive, game-based learning',
    numeracyStudentsCount: '360+ students building confidence in math',
    teacherTrainingImpact: 'Teacher Training',
    teacherTrainingImpactDesc: 'Empowering educators with modern instructional methods',
    teacherTrainingCount: '20+ teachers trained in TaRL',

    // Communities Section
    communitiesWeServe: 'Communities We Serve',
    communitiesWeServeDesc: 'Reaching schools with the greatest need for support',
    papuaWestPapua: 'Papua & West Papua',
    studentsCount: 'students',
    schoolsCount: 'schools',

    // Stories of Change
    storiesOfChange: 'Stories of Change',
    storiesOfChangeDesc: 'Real transformations from real people',
    parentTestimonial:
      'After just three months, my daughter went from struggling with basic words to reading full sentences. She now reads bedtime stories to her younger brother.',
    parentName: '— Sari, Parent, SD Inpres 01 Sorong',
    teacherTestimonial:
      'TaRL training changed everything. I now focus on meeting students at their level instead of rushing through the syllabus.',
    teacherName: '— Ahmad, Teacher, SDN 05 Jakarta Selatan',
    principalTestimonial:
      'Our new reading room has become the most loved space at school. Students come early just to read. It feels magical.',
    principalName: '— Sister Maria, Principal, SD Katolik Ende',

    // How We Measure
    howWeMeasureImpact: 'How We Measure Impact',
    howWeMeasureDesc: 'Every program includes transparent, evidence-based evaluation',
    baselineAssessments: 'Baseline Assessments',
    baselineAssessmentsDesc: 'Measuring where students start',
    ongoingTracking: 'Ongoing Tracking',
    ongoingTrackingDesc: 'Weekly & monthly evaluations',
    finalAssessment: 'Final Assessment',
    finalAssessmentDesc: 'End-of-program learning outcomes',
    transparentReportingImpact: 'Transparent Reporting',
    transparentReportingImpactDesc: 'Donors receive full impact reports',

    // Learning Gains
    averageLearningGains: 'Average Learning Gains',
    literacyGain: 'Literacy',
    numeracyGain: 'Numeracy',
    learningBenchmarkDesc: 'Percentage of students reaching age-appropriate learning benchmarks',

    // CTA
    bePartOfImpact: 'Be Part of Our Impact',
    bePartOfImpactDesc:
      'Every contribution creates long-term change for students across Indonesia.',
    makeADonation: 'Make a Donation',
    browsePrograms: 'Browse Programs',

    // Volunteer Page
    volunteerHeroTitle: 'Become an Accelero Volunteer',
    volunteerHeroSubtitle:
      'Help create more equitable education, where every child has the opportunity to learn, with no one left behind.',
    whyVolunteer: 'Why Volunteer With Us?',
    makeAnImpact: 'Meaningful Impact',
    makeAnImpactDesc:
      'Contribute directly to on-the-ground programs that improve learning quality for students and teachers.',
    learnAndGrow: 'Learn & Grow',
    learnAndGrowDesc:
      'Develop teaching skills, communication, leadership, classroom management, project operations, and teamwork.',
    connect: 'Build Connections',
    connectDesc:
      'Join a diverse community of individual volunteers and partner universities committed to educational equity.',
    empower: 'Empower Schools',
    empowerDesc: 'Support schools in strengthening academic and non-academic programs.',
    volunteerCategories: 'Volunteer Categories',
    individualVolunteers: 'Individual Volunteers',
    individualVolunteersDesc:
      'Open for university students, young professionals, and anyone passionate about education.',
    universityVolunteers: 'University Partner Volunteers',
    universityVolunteersDesc:
      'Structured volunteer programs for students from Accelero’s partner universities.',
    volunteerRoles: 'Volunteer Roles',
    academicVolunteers: 'Academic Volunteers',
    roleTeacher: 'Teachers (Numeracy, Literacy)',
    roleSpecialEd: 'Special Education Teachers',
    roleCurriculum: 'Curriculum Support',
    nonAcademicVolunteers: 'Non-Academic Volunteers',
    roleFieldOps: 'Field Operations (Project Manager)',
    roleDocumentation: 'Documentation (photo & video)',
    roleCommunity: 'Community Engagement Support',
    allRolesOnField: 'All roles are on-field and placed in Accelero partner schools.',

    // Volunteer Form
    volunteerApplication: 'Volunteer Application Form',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    volunteerCategory: 'Volunteer Category',
    categoryIndividual: 'Individual',
    categoryUniversity: 'University Partner',
    preferredRoles: 'Preferred Roles',
    selectAtLeastOne: 'Select at least one:',
    formRoleTeacher: 'Teacher (Numeracy / Literacy)',
    formRoleSpecialEd: 'Special Education Teacher',
    formRoleCurriculum: 'Curriculum Support',
    formRoleFieldOps: 'Field Operations',
    formRoleDocumentation: 'Documentation',
    formRoleCommunity: 'Community Engagement Support',
    relevantExperience: 'Relevant Experience (Optional)',
    whyVolunteerQuestion: 'Why Do You Want to Volunteer?',
    submitApplication: 'Submit Application',
    submitting: 'Submitting...',
    experiencePlaceholder:
      'Tell us about teaching experience, social activities, technical skills, or any relevant background.',
    motivationPlaceholder: 'Share your motivation for joining Accelero.',
    required: '*',

    // Messages
    thankYouVolunteer: 'Thank you for your interest! We will contact you soon.',
    somethingWentWrong: 'Something went wrong. Please try again.',
    failedToSubmit: 'Failed to submit. Please try again later.',

    // About Page
    aboutAccelero: 'About Accelero',
    aboutHeroTitle: 'Creating Equal Educational Opportunities for Every Child in Indonesia',
    aboutHeroSubtitle:
      'Accelero was built from a deep awareness of the learning gap experienced by children across Indonesia’s rural regions. Many have extraordinary potential, yet fall behind simply because they lack strong foundational skills early on. We exist to bridge that gap—strengthening literacy and numeracy at the primary level so that every child, no matter where they’re born, has an equal opportunity to grow, learn, and pursue the future they deserve.',

    ourStory: 'Our Story',
    bornFromExperience: 'Rooted in the Experiences We Once Lived',
    ourStoryText1:
      'Accelero was born from our own journey—growing up in rural Indonesia and witnessing firsthand the educational gap between rural and urban communities. When we moved away to pursue higher education, we realized that many children fall behind not because they lack potential, but because their foundational learning was never fully strengthened.',
    ourStoryText2:
      'That realization inspired us to build Accelero—a community-led movement to ensure children in remote areas no longer face the same disadvantages. We believe every child, no matter where they are born, deserves strong literacy and numeracy foundations to pursue their dreams with confidence.',
    dPrizeRecipient: 'Winner of the D-Prize Global Competition 2025',
    dPrizeDesc:
      'Recognized internationally for our innovative approach to scaling evidence-based education solutions in underserved communities.',

    ourMission: 'Our Mission',
    missionText:
      'To accelerate foundational literacy and numeracy among children in Indonesia’s most under-resourced communities through evidence-based teaching and community empowerment.',
    ourVision: 'Our Vision',
    visionText:
      'An Indonesia where no child is left behind because of where they were born—where every child has access to quality education and the opportunity to thrive.',

    whatWeDo: 'What We Do',
    evidenceBasedEducation: 'Evidence-Based Education, Community-Powered Impact',
    whatWeDoDesc:
      'We bring the globally proven Teaching at the Right Level (TaRL) approach to underserved schools in remote areas of Indonesia.',

    afterSchoolPrograms: 'After-School Learning Program',
    afterSchoolDesc:
      'Supporting Grade 3–6 students who are still behind in reading and math by teaching them based on their actual learning level—not their grade level.',
    universityVolunteer: 'University Volunteer Training',
    universityVolunteerDesc:
      'Partnering with local universities to recruit and train student volunteers as teaching mentors who strengthen their own communities.',
    literacyRichEnvironments: 'Building Literacy-Rich Environments',
    literacyRichDesc:
      'Providing storybooks and learning materials through donations and local partnerships to create joyful and engaging learning spaces.',
    communityPartnerships: 'Community Partnerships',
    communityPartnershipsDesc:
      'Working closely with schools, local education offices, and universities to build an inclusive and sustainable learning ecosystem.',
    aboutImpactTitle: 'Accelero’s Impact',
    aboutImpactDesc: 'Real progress, powered by community and growing every day.',
    currentAchievements: 'Current Achievements',
    achChildren: 'Children Supported',
    achChildrenDesc:
      'Receiving literacy and numeracy guidance through our after-school learning sessions.',
    achTeachers: 'Trained Community Teachers',
    achTeachersDesc:
      'University volunteers and pre-service teachers trained in TaRL and hands-on teaching practice.',
    achSchools: 'Partner Schools',
    achSchoolsDesc:
      'Working directly with elementary schools to strengthen students’ learning foundations.',
    achUniversities: 'Partner Universities',
    achUniversitiesDesc:
      'Collaborating with local universities to develop young, evidence-based educators.',
    achLiteracy: 'Literacy Improvement',
    achLiteracyDesc: 'Students advancing in reading proficiency within just weeks of intervention.',
    achNumeracy: 'Numeracy Improvement',
    achNumeracyDesc:
      'Significant progress in basic math skills after participating in the program.',
    goals2026: 'Goals for 2026',
    goals2026Desc:
      'Expanding our reach to bring quality foundational learning to more children across Indonesia.',
    goalChildrenNumber: '5,000+',
    goalChildren: 'Children Reached',
    goalChildrenDesc:
      'Targeting expansion to 5 provinces through a scalable, community-driven model.',
    goalTeachersNumber: '500+',
    goalTeachers: 'Trained Community Teachers',
    goalTeachersDesc:
      'Volunteers and pre-service teachers empowered through evidence-based training and real teaching experience.',

    whyDifferent: "Why We're Different",
    sustainableCommunityModel: 'A Sustainable, Community-Driven Model',
    equitableAccess: 'Equitable Access to Education',
    equitableAccessDesc: 'Every child deserves quality education—no matter where they live.',
    localTalent: 'Local Talent, Locally Empowered',
    localTalentDesc:
      'We recruit and develop educators from within the communities we serve, ensuring cultural relevance, local ownership, and long-term sustainability.',
    replicableModel: 'A Replicable Program Model',
    replicableModelDesc:
      'Through strong partnerships with local universities, our model can be expanded across regions in Indonesia, creating a multiplier effect for education impact.',
    sustainableApproach: 'Sustainable Approach',
    sustainableApproachDesc:
      'We build community capacity, not dependency—ensuring the impact continues even after the program ends.',
    holisticSupport: 'Holistic Support for Learning',
    holisticSupportDesc:
      'Transforming education goes beyond curriculum. We combine evidence-based teaching, strong literacy culture, and supportive school environments into one cohesive approach.',

    ourTeam: 'Our Team',
    meetTheFounders: 'Meet the Founders',
    passionateEducators:
      'Driven by personal experiences and a deep commitment to creating more equitable educational opportunities for children across Indonesia.',
    catherineRole: 'Founder, Program & Partnerships Director',
    catherineBio:
      'Leads Accelero’s on-the-ground programs, including designing and running after-school learning sessions using the Teaching at the Right Level (TaRL) approach. Catherine also strengthens partnerships with local schools and universities, ensuring every child has a meaningful and high-quality learning experience.',
    kevinRole: 'Co-Founder & Strategic Program Lead',
    kevinBio:
      'Leads Accelero’s growth and expansion strategies, focusing on program innovation, building community-powered learning ecosystems, and designing approaches to extend educational impact to children across Indonesia.',

    joinTheMovement: 'Join the Movement',
    joinMovementDesc:
      'Are you a student, educator, donor, or partner organization? Join Accelero in ensuring every child in Indonesia has the chance to learn and thrive.',
    supportOurMission: 'Support Our Mission',
    getInTouch: 'Get in Touch',

    // Contact Page
    contactHeroTitle: "We'd Love to Hear From You",
    contactHeroSubtitle:
      'Have questions about our programs? Interested in volunteering or exploring a partnership? Our team is here to help and happy to connect.',

    emailUs: 'Email Us',
    emailUsDesc: 'We typically respond within 24 hours.',
    whatsApp: 'WhatsApp',
    whatsAppDesc: 'For quick questions, reach us directly at:',
    officeLocation: 'Office Location',
    officeLocationDesc: 'Jakarta, Indonesia (Remote Operations)',
    workingRemotely: 'Working flexibly across multiple regions nationwide.',

    sendUsMessage: 'Send Us a Message',
    sendUsMessageDesc: 'Fill out the form below, and we’ll get back to you shortly.',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Your full name',
    emailAddress: 'Email Address',
    emailPlaceholder: 'your@email.com',
    phoneNumber: 'Phone Number',
    phonePlaceholder: '+62 xxx xxxx xxxx',
    subject: 'Subject',

    generalInquiry: 'General Inquiry',
    volunteerOpportunity: 'Volunteering',
    partnership: 'Partnership',
    donationQuestion: 'Donation',
    mediaPress: 'Media/Press',
    other: 'Other',

    yourMessage: 'Your Message',
    messagePlaceholder: 'Tell us more about your inquiry...',
    sending: 'Sending...',
    sendMessage: 'Send Message',

    thankYouContact: "Thank you for contacting us! We'll get back to you within 24 hours.",
    contactError: 'Something went wrong. Please try again or email us directly.',

    quickConnect: 'Quick Connect',
    quickConnectDesc: 'Prefer a faster response?',
    messageOnWhatsApp: 'Message on WhatsApp',
    sendEmail: 'Send an Email',

    officeHours: 'Office Hours',
    mondayFriday: 'Monday – Friday',
    hoursWeekday: '9:00 AM – 6:00 PM (WIB)',
    saturday: 'Saturday',
    hoursSaturday: '10:00 AM – 4:00 PM (WIB)',
    sunday: 'Sunday',
    closed: 'Closed',

    needHelp: 'Need Help?',
    needHelpDesc: 'Visit our FAQ page for quick answers to common questions.',
    visitHelpCenter: 'Go to Help Center →',

    faq: 'FAQ',
    frequentlyAskedQuestions: 'Frequently Asked Questions',

    howCanIVolunteer: 'How can I volunteer with Accelero?',
    howCanIVolunteerAnswer:
      'Accelero welcomes individuals as well as university students through partner programs to support children’s education in underserved areas. Volunteers can assist in academic support or non-academic roles, all carried out on-field at Accelero partner schools. Registration is available through the volunteer page.',

    howDoIMakeDonation: 'How do I make a donation?',
    howDoIMakeDonationAnswer:
      'You can donate securely through our donation page. All contributions are tracked transparently.',

    canMyOrganizationPartner: 'Can my organization partner with Accelero?',
    canMyOrganizationPartnerAnswer:
      'Absolutely. We welcome partnerships with schools, universities, NGOs, and corporations. Contact us with “Partnership” as the subject.',

    howDoIGetUpdates: 'How do I stay updated about your programs?',
    howDoIGetUpdatesAnswer:
      'Subscribe to our newsletter via the donation page or follow our social media channels for regular updates.',

    joinOurMission: 'Join Our Mission',
    joinOurMissionDesc:
      'There are many ways to support educational equity—through volunteering, donating, or partnering with us.',
    viewOurPrograms: 'Browse Our Programs',
    contactDonateCta: 'Make a Donation',

    // Blog Page
    ourBlog: 'Our Blog',
    storiesOfImpact: 'Stories of Impact',
    blogHeroSubtitle:
      'Discover the latest news, impact stories, and updates from our work in education and community development.',
    searchArticles: 'Search articles...',
    allPosts: 'All Posts',
    impactStoriesCategory: 'Impact Stories',
    updatesCategory: 'Updates',
    eventsCategory: 'Events',
    teamNewsCategory: 'Team News',
    clearFilters: 'Clear Filters',
    showing: 'Showing',
    of: 'of',
    articles: 'articles',
    noArticlesFound: 'No Articles Found',
    noArticlesMessage:
      "We couldn't find any articles matching your criteria. Try adjusting your filters or search query.",
    stayUpdated: 'Stay Updated',
    subscribeNewsletter:
      'Subscribe to our newsletter to receive the latest impact stories, program updates, and volunteer opportunities directly in your inbox.',
    enterYourEmail: 'Enter your email',
    subscribeBtn: 'Subscribe',
    minRead: 'min read',

    //Donation Page
    makeAnImpactDonate: 'Make an Impact',
    everyDonationChangesLives: 'Every Donation Changes Lives',
    donatePageDesc:
      'Your contribution provides books, training, and hope to students who need it most. 100% transparent, 100% impactful.',
    chooseYourProject: 'Choose Your Project',
    supportProject: 'Support',
    generalFund: 'General Fund - Where Most Needed',
    chooseYourAmount: 'Choose Your Amount',
    oneTime: 'One-Time',
    monthly: 'Monthly',
    monthlyInfo: 'Monthly donations help us plan better and create lasting impact',
    orEnterCustomAmount: 'Or enter custom amount',
    enterAmount: 'Enter amount',
    minimumDonation: 'Minimum donation: Rp 10,000',
    shareYourMotivation: 'Share Your Motivation',
    whatInspiredYou: 'What inspired you to give today?',
    selectMotivation: 'Select your motivation...',
    believeInEducationEquity: 'I believe in education equity',
    supportingCommunity: 'Supporting my community',
    wantMeasurableImpact: 'Want to see measurable impact',
    investingInFuture: "Investing in children's future",
    taxDeductible: 'Tax-deductible donation',
    messageOfEncouragement: 'Message of Encouragement',
    optional: '(Optional)',
    sendMessageToStudents: 'Send a message to the students and teachers...',
    yourInformation: 'Your Information',
    enterFullName: 'Enter your full name',
    yourEmail: 'your@email.com',
    phoneNumberOptional: 'Phone Number',
    phoneNumberPlaceholder: '+62 xxx xxxx xxxx',
    makeDonationAnonymous: 'Make my donation anonymous',
    sendMonthlyUpdates: 'Send me monthly impact updates',
    proceedToPayment: 'Proceed to Payment',
    processing: 'Processing...',
    donationSecure: 'Your donation is secure and encrypted. We never store your payment details.',
    donationSummary: 'Donation Summary',
    donationType: 'Donation Type',
    oneTimeDonation: 'One-Time',
    monthlyRecurring: 'Monthly Recurring',
    amount: 'Amount',
    supporting: 'Supporting',
    yourImpactSummary: 'Your Impact',
    seeHowDonationHelps2: 'See how your donation helps:',
    annualTotal: 'Annual total',
    whyDonateWithUs: 'Why Donate with Us?',
    hundredPercentTransparent: '100% Transparent',
    trackDonation: 'Track exactly where your donation goes',
    taxDeductibleReceipt: 'Tax Deductible',
    receiveOfficialReceipt: 'Receive official donation receipt',
    securePayment: 'Secure Payment',
    bankLevelEncryption: 'Bank-level encryption',
    frequentlyAskedQuestionsDonate: 'Frequently Asked Questions',
    howIsMyDonationUsed: 'How is my donation used?',
    howIsMyDonationUsedAnswer:
      '100% of your donation goes directly to program costs - books, teacher training, and student materials. Administrative costs are covered separately.',
    willIReceiveReceipt: 'Will I receive a receipt?',
    willIReceiveReceiptAnswer:
      "Yes! You'll receive an email receipt immediately after donation, which is valid for tax deduction purposes.",
    canICancelMonthly: 'Can I cancel monthly donations?',
    canICancelMonthlyAnswer:
      'Absolutely. You can cancel or modify your monthly donation at any time from your donor dashboard.',
    howTrackImpact: 'How do I track impact?',
    howTrackImpactAnswer:
      'We send monthly updates with photos, stories, and measurable results from the programs you support.',
    studentsFunded: 'students funded',
    canTrainTeachers: 'can train',
    teachers: 'teacher(s) in TaRL methodology',
    canSupport: 'can support',
    studentsForMonth: 'students for one month',
    canProvide: 'can provide',
    readingBooks: 'reading books',
    willMakeRealDifference: "will make a real difference in a student's life",
    minimumDonationAlert: 'Minimum donation is Rp 10,000',
    fillNameEmail: 'Please fill in your name and email',

    // QRIS Payment Modal
    scanQRToDonate: 'Scan QR Code to Donate',
    qrisInstructions:
      'Open your mobile banking or e-wallet app and scan this QR code to complete your donation.',
    donationAmount: 'Donation Amount',
    scanWithAnyApp: 'Scan with any banking app or e-wallet (GoPay, OVO, Dana, ShopeePay, etc.)',
    afterPayment:
      'After completing payment, please enter the account number or phone number you sent from:',
    senderAccountNumber: 'Your Account Number or Phone number',
    enterSenderAccount: 'Enter your bank account number or phone number',
    accountNumberPlaceholder: 'e.g., 1234567890',
    confirmDonation: 'Confirm Donation',
    closeModal: 'Close',
    accountNumberRequired: 'Please enter your account number',
    thankYouForDonation:
      'Thank you for your donation! We will verify your payment and send a confirmation email shortly.',
    orTransferDirectly: 'Or transfer directly to our bank account:',
    bankName: 'Bank',
    accountName: 'Account Name',
    copyAccountNumber: 'Copy Account Number',
    accountCopied: 'Account number copied!',

    // Footer
    footerMission:
      'Empowering underserved communities in Indonesia through quality education. Every child deserves the right to learn.',
    aboutUs: 'About Us',
    impactStories: 'Impact Stories',
    programs: 'Programs',
    resources: 'Resources',
    helpCenter: 'Help Center',
    newsletterDescription: 'Get monthly updates on our programs and see the impact of your support',
    subscribe: 'Subscribe',
    allRightsReserved: 'All rights reserved',
    secureDonations: 'Secure Donations',
    verifiedNonprofit: 'Verified Nonprofit',
    transparency: 'Transparency',
  },
  id: {
    // Navigation
    home: 'Beranda',
    about: 'Tentang',
    projects: 'Proyek',
    blog: 'Blog',
    contact: 'Kontak',
    donate: 'Donasi',
    volunteer: 'Relawan',
    members: 'Anggota',
    partner: 'Mitra',
    impact: 'Dampak',
    help: 'Bantuan',

    // Common
    learnMore: 'Pelajari Lebih Lanjut',
    readMore: 'Baca Selengkapnya',
    getStarted: 'Mulai',
    submit: 'Kirim',
    cancel: 'Batal',
    save: 'Simpan',
    edit: 'Edit',
    delete: 'Hapus',
    search: 'Cari',
    loading: 'Memuat...',

    // Hero Section
    heroTitle1: 'Setiap Anak Berhak Belajar Sesuai Kemampuannya',
    heroTitle2: 'Membangun Pondasi Belajar yang Kuat untuk Masa Depan Anak Indonesia',
    heroTitle3: 'Membantu Anak Menemukan Kepercayaan Diri Melalui Belajar',
    heroSubtitle1:
      'Bersama, kita bantu anak-anak Indonesia membaca, berhitung, dan percaya diri melalui program Teaching at the Right Level (TaRL) — agar tak ada lagi yang tertinggal di kelas.',
    heroSubtitle2:
      'Kami mendampingi anak-anak di daerah terpencil agar tetap memiliki akses pada pembelajaran dasar yang berkualitas.',
    heroSubtitle3:
      'Dukungan Anda mempercepat perubahan nyata dalam kemampuan membaca, berhitung, dan kepercayaan diri anak-anak di daerah tertinggal.',
    donateNow: 'Donasi Sekarang',
    makeImpactToday: 'Buat dampak hari ini',
    viewPrograms: 'Lihat Program Kami',
    seeWhereWeWork: 'Lihat lokasi kami',
    scrollToExplore: 'Gulir untuk menjelajah',

    // Impact Stats
    ourImpact: 'Dampak Kami',
    makingRealDifference: 'Mewujudkan Perubahan Nyata',
    impactStatsDesc:
      'Perubahan besar dimulai dari langkah kecil bersama. Inilah hasil nyata dari dukungan Anda bagi anak-anak Indonesia.',
    booksDistributed: 'Buku Telah Didistribusikan',
    booksDistributedDesc: 'Menyediakan bahan bacaan menarik dan sesuai level untuk setiap anak.',
    teachersTrained: 'Guru Telah Dilatih',
    teachersTrainedDesc: 'Membekali pendidik dengan pendekatan Teaching at the Right Level (TaRL).',
    regionsServed: 'Wilayah Telah Dijangkau',
    regionsServedDesc: 'Memulai langkah perubahan melalui kolaborasi dengan komunitas lokal.',
    schoolsSupported: 'Sekolah Telah Terbantu',
    schoolsSupportedDesc:
      'Mendukung sekolah dengan fasilitas, materi, dan pendampingan pembelajaran.',
    totalFunding: 'Dana Terkumpul',
    totalFundingDesc:
      'Berkat para donatur dan mitra yang percaya pada kesempatan belajar yang setara.',
    totalFundingAmount: 'Rp 285 Juta',
    annualGoalProgress: 'Progres Target 2026',
    annualGoalProgressDesc: 'Kami menargetkan menjangkau 5.000 siswa pada tahun 2026.',
    goalProgressStatus: 'Saat ini, 216 siswa telah terbantu — langkah awal yang terus berkembang.',
    studentsReached: 'Siswa Terjangkau',

    // How It Works
    simpleTransparent: 'Sederhana & Transparan',
    howDonationCreatesChange: 'Bagaimana Donasi Anda Mengubah Hidup',
    howDonationDesc:
      'Dari niat baik Anda hingga keberhasilan siswa, setiap langkahnya tercatat dan bisa Anda pantau.',
    chooseYourImpact: 'Pilih Dampak yang Ingin Anda Ciptakan',
    chooseYourImpactDesc: 'Telusuri sekolah dan program yang saat ini membutuhkan dukungan Anda.',
    chooseDetail1: 'Lihat profil sekolah',
    chooseDetail2: 'Ketahui kebutuhan spesifiknya',
    chooseDetail3: 'Pantau progres secara langsung',
    donateSecurely: 'Donasi dengan Aman',
    donateSecurelyDesc:
      'Beri dukungan satu kali atau rutin setiap bulan lewat QRIS atau kartu kredit.',
    donateDetail1: '100% aman',
    donateDetail2: 'Bisa sertakan pesan pribadi',
    trackProgress: 'Pantau Perkembangannya',
    trackProgressDesc: 'Dapatkan kabar terbaru dari lapangan secara transparan.',
    trackDetail1: 'Laporan dampak bulanan',
    trackDetail2: 'Foto & cerita siswa',
    trackDetail3: 'Pembaruan progres berkala',
    seeResults: 'Lihat Hasil Nyata',
    seeResultsDesc:
      'Saksikan bagaimana dukungan Anda membantu siswa belajar, berkembang, dan mewujudkan mimpi.',
    seeDetail1: 'Hasil terukur',
    seeDetail2: 'Perbandingan sebelum dan sesudah',
    seeDetail3: 'Dampak bagi komunitas sekitar',
    readyToMakeDifference2: 'Siap Membuat Perubahan?',
    readyToMakeDifferenceDesc2:
      'Bergabunglah dengan komunitas donatur dan relawan yang ikut mengubah wajah pendidikan di Indonesia.',
    startDonating: 'Mulai Berdonasi',
    startDonatingDesc: 'Dukung sekolah dan siswa untuk berkembang.',
    becomeVolunteer: 'Jadi Relawan',
    becomeVolunteerDesc: 'Berbagi waktu, keahlian, dan kepedulian Anda.',

    // Volunteer CTA
    volunteerCtaDesc:
      'Mari bersama memperkuat fondasi pendidikan di seluruh Indonesia. Baik sebagai individu, universitas, maupun organisasi — keterlibatan Anda akan membawa dampak nyata.',
    closingCtaTitle: 'Jadi Bagian dari Perubahan',
    fieldVolunteerTitle: 'Relawan Lapangan',
    fieldVolunteerDesc:
      'Bergabunglah dengan tim relawan Accelero Indonesia yang bekerja langsung di sekolah dan komunitas untuk meningkatkan kemampuan literasi dan numerasi siswa.',
    academicRoles: 'Peran Akademik',
    academicRolesDesc:
      'Mengajar literasi, numerasi, dan memberikan dukungan tambahan bagi siswa yang membutuhkan.',
    nonAcademicRoles: 'Peran Non-Akademik',
    nonAcademicRolesDesc:
      'Mendukung operasional program serta melakukan dokumentasi kegiatan di lapangan.',
    whoCanJoin: 'Siapa yang Bisa Bergabung',
    whoCanJoinDesc: 'Individu atau universitas mitra yang siap berkontribusi langsung di lapangan.',
    applyToVolunteer: 'Daftar Jadi Relawan',

    // Partnership
    partnerWithUs: 'Jadi Mitra Kami',
    createLastingImpact: 'Ciptakan Dampak yang Berkelanjutan',
    getInvolved: 'Bergabung Bersama Kami',
    partnershipDesc:
      'Berkolaborasi dengan Accelero Indonesia melalui program CSR atau kemitraan institusi untuk meningkatkan fasilitas sekolah, menyediakan bahan ajar yang tepat, dan memastikan setiap siswa mendapat kesempatan belajar yang setara.',
    partnershipDesc2:
      'Kemitraan Anda membantu menghadirkan pendidikan yang lebih adil bagi anak-anak di seluruh Indonesia.',
    strategicAlignment: 'Keselarasan Strategis',
    strategicAlignmentDesc: 'Kemitraan yang disesuaikan dengan tujuan CSR Anda',
    globalReach: 'Jangkauan Global',
    globalReachDesc: 'Program di berbagai komunitas',
    becomeAPartner: 'Jadi Mitra',
    partnerHeroCta: 'Jadi Mitra Kami',
    learnAboutUs: 'Tentang Accelero',
    partnershipImpact: 'Dampak Kemitraan',
    buildingStrongerCommunities: 'Membangun komunitas yang lebih kuat bersama',
    activePartners: 'Mitra Aktif',
    jointFunding: 'Pendanaan Bersama',
    livesImpacted: 'Kehidupan Terbantu',
    partnershipOpportunities: 'Peluang Kemitraan',
    corporatePartnership: 'Kemitraan Korporasi (CSR / Employee Engagement)',
    corporatePartnershipDesc: 'Inisiatif CSR strategis',
    foundationPartnership: 'Kemitraan Foundation / Yayasan',
    foundationPartnershipDesc:
      'Kolaborasi untuk memperluas dampak pendanaan pada pendidikan dasar.',
    ngoPartnership: 'Kemitraan NGO / Komunitas',
    ngoPartnershipDesc: 'Program berbagi sumber daya',
    available: 'Tersedia',
    trustedBy: 'Dipercaya Oleh',

    // Partnership Page
    partnershipHeroTitle: 'Bermitra dengan Accelero',
    partnershipHeroSubtitle:
      'Bersama, kita dapat menghadirkan akses pendidikan yang lebih baik bagi ribuan siswa di seluruh Indonesia.',
    whyPartner: 'Mengapa Bermitra dengan Accelero?',
    meaningfulImpact: 'Dampak Nyata & Terukur',
    meaningfulImpactDesc:
      'Dukung program yang terbukti meningkatkan literasi, numerasi, dan kualitas belajar di sekolah-sekolah yang paling membutuhkan.',
    transparentReporting: 'Laporan Transparan',
    transparentReportingDesc:
      'Dapatkan laporan lengkap berisi foto, data perkembangan, dan hasil akhir program.',
    accessToSchools: 'Akses ke Komunitas Sekolah',
    accessToSchoolsDesc:
      'Program kami sudah berjalan di berbagai wilayah dan menjangkau sekolah-sekolah underserved di seluruh Indonesia.',
    brandVisibility: 'Eksposur & Reputasi Brand',
    brandVisibilityDesc:
      'Perkuat inisiatif CSR Anda melalui kolaborasi strategis yang relevan, berdampak, dan berkelanjutan.',

    // Partnership Types
    corporatePartnershipFull:
      'Untuk perusahaan yang ingin berkontribusi langsung pada peningkatan pendidikan.',
    corporateFeature1: 'Program literasi & numerasi',
    corporateFeature2:
      'Revitalisasi infrastruktur sekolah (perpustakaan, ruang baca, alat belajar)',
    corporateFeature3: 'Employee volunteering on-site',
    corporateFeature4: 'Program donasi rutin atau matching fund',
    universityPartnership: 'Kemitraan Universitas',
    universityPartnershipDesc:
      'Untuk kampus yang ingin mengembangkan community service, internship, dan service learning.',
    universityFeature1: 'Program pengabdian masyarakat',
    universityFeature2: 'Volunteer (on-field teaching & project support)',
    universityFeature3: 'Kolaborasi penelitian pendidikan',
    universityFeature4: 'Program beasiswa untuk guru atau sekolah mitra',
    foundationPartnershipFull:
      'Kolaborasi untuk memperluas dampak pendanaan pada pendidikan dasar.',
    foundationFeature1: 'Co-funding program',
    foundationFeature2: 'Pengembangan model program berbasis data',
    foundationFeature3: 'Pengukuran dampak jangka panjang',
    foundationFeature4: 'Ekspansi program ke daerah baru',
    ngoPartnershipFull:
      'Kolaborasi antar-organisasi untuk memperkuat eksekusi program di lapangan.',
    ngoFeature1: 'Joint program',
    ngoFeature2: 'Berbagi sumber daya dan keahlian',
    ngoFeature3: 'Community mobilization',
    ngoFeature4: 'Pemerataan akses pendidikan',

    // Partnership Stories
    partnershipSuccessStories: 'Kisah Keberhasilan Kemitraan',
    dPrizeStoryDesc:
      'D-Prize memberikan dukungan pendanaan dan pendampingan untuk membantu Accelero menghadirkan pendekatan Teaching at the Right Level (TaRL) melalui After School Program di sekolah dasar mitra—dimulai dari Sorong, Papua Barat Daya, salah satu kota dengan tingkat literasi terendah di Indonesia.',
    dPrizeQuote:
      '“Congratulations to D-Prize winner, Accelero Indonesia! Led by Catherine Tikara and Kevin Evannanda Septian, Accelero delivers the Teaching at the Right Level (TaRL) approach through after-school programs in rural primary schools… Through their partnership with local universities, they recruit and train student volunteers, providing them with hands-on teaching experience while empowering them to serve their communities. At the end of its pilot, Accelero would have reached 200+ learners.”',
    dPrizeAttribution: '— D-Prize',

    // Partnership Process
    howPartnershipWorks: 'Bagaimana Proses Kemitraan Berjalan',
    initialConsultation: 'Konsultasi Awal',
    initialConsultationDesc:
      'Kami mempelajari tujuan, nilai, dan area dampak yang ingin Anda capai.',
    customizedProposal: 'Proposal Kemitraan',
    customizedProposalDesc:
      'Anda menerima proposal yang disesuaikan: target, aktivitas, timeline, dan anggaran.',
    implementation: 'Implementasi Program',
    implementationDesc:
      'Program dijalankan dengan tim lapangan Accelero, volunteer, dan mitra sekolah.',
    impactReporting: 'Laporan Dampak',
    impactReportingDesc:
      'Setiap tahap dipantau dan dilaporkan secara transparan, lengkap dengan data hasil.',
    readyToCreateImpact: 'Siap Menciptakan Perubahan Bersama?',
    readyToCreateImpactDesc:
      'Mari bangun kolaborasi yang memberikan dampak jangka panjang untuk pendidikan Indonesia.',

    // CTA
    startConversation: 'Mulai Percakapan',
    supportSchool:
      'Bantu sekolah-sekolah yang membutuhkan ruang belajar dan membaca yang lebih layak.',
    featuredPrograms: 'Program Unggulan',
    ourPrograms: 'Program Kami',
    viewAllPrograms: 'Lihat Semua Program',
    supportThisProject: 'Dukung Program Ini',
    featuredProgramCta: 'Donasi Sekarang',
    viewImpact: 'Lihat Dampak',

    // Projects Page
    makeADifferenceToday: 'Mari Wujudkan Perubahan Hari Ini',
    browseOurPrograms:
      'Jelajahi program kami dan bantu sekolah mitra menciptakan lingkungan belajar yang mendukung kemampuan dasar anak. Setiap donasi digunakan secara transparan—dan langsung membantu menyediakan ruang belajar yang lebih layak, buku yang relevan, serta fasilitas yang mendukung budaya membaca di sekolah.',
    activePrograms: 'Program Aktif',
    totalRaised: 'Dana Terkumpul',
    searchPrograms: 'Cari program...',
    allPrograms: 'Semua Program',
    literacy: 'Literasi',
    numeracy: 'Numerasi',
    teacherTraining: 'Pelatihan Guru',
    infrastructure: 'Infrastruktur',
    allStatus: 'Semua Status',
    active: 'Aktif',
    completed: 'Selesai',
    newestFirst: 'Terbaru',
    lowestFunded: 'Pendanaan Terendah',
    highestFunded: 'Pendanaan Tertinggi',
    mostImpact: 'Dampak Terbesar',
    clear: 'Hapus',
    results: 'hasil',
    result: 'hasil',
    noProgramsFound: 'Program Tidak Ditemukan',
    noProgramsFoundDesc:
      'Kami tidak dapat menemukan program yang sesuai dengan kriteria Anda. Coba sesuaikan filter atau kata kunci pencarian Anda.',
    noActiveProgramsDesc: 'Saat ini kami tidak memiliki program aktif. Periksa kembali nanti!',
    resetFilters: 'Reset Filter',
    cantFindWhatYoureLookingFor: 'Tidak Dapat Menemukan yang Anda Cari?',
    donateToGeneralFundDesc:
      'Buat donasi umum ke dana kami dan kami akan mengalokasikannya ke program yang paling membutuhkannya.',
    donateToGeneralFund: 'Donasi ke Dana Umum',
    contactUs: 'Hubungi Kami',
    wantToMakeEvenBiggerImpact: 'Ingin Memberi Dampak yang Lebih Besar?',
    supportGeneralFundDesc:
      'Dukung Dana Pendidikan Umum kami untuk memastikan Program TaRL After School dapat berjalan di lebih banyak sekolah mitra.',

    // Project Detail Page
    aboutThisProgram: 'Tentang Program Ini',
    theChallenge: 'Tantangan yang Dihadapi',
    whyThisMatters: 'Mengapa Ini Penting',
    ourApproachProject: 'Pendekatan Kami',
    expectedImpact: 'Dampak yang Diharapkan',
    aboutTheSchool: 'Tentang Sekolah',
    overview: 'Ringkasan',
    updates: 'Pembaruan',
    budgetBreakdown: 'Rincian Anggaran',
    timeline: 'Jadwal',
    totalBudget: 'Total Anggaran',
    ofTotalBudget: 'dari total anggaran',
    noUpdatesYet: 'Belum Ada Pembaruan',
    noUpdatesDesc: 'Periksa kembali untuk pembaruan progres program ini',
    budgetComingSoon: 'Detail Anggaran Segera Hadir',
    budgetComingSoonDesc: 'Rincian anggaran terperinci akan segera tersedia',
    timelineComingSoon: 'Jadwal Segera Hadir',
    timelineComingSoonDesc: 'Milestone program akan ditambahkan seiring berjalannya proyek',
    raised: 'Terkumpul',
    goal: 'Target',
    funded: 'terdanai',
    stillNeeded: 'lagi untuk tercapai',
    donors: 'Donatur',
    volunteers: 'Relawan',
    shareThisProject: 'Bagikan Proyek Ini',
    projectCompleted: 'Proyek Selesai',
    secureTransparentDonations: 'Donasi aman & transparan',
    yourImpact: 'Dampak Anda',
    seeHowDonationHelps: 'Lihat bagaimana donasi Anda membantu:',
    providesReadingBooks: 'Menyediakan 10 buku bacaan',
    supportsStudentsMonth: 'Mendukung 5 siswa selama 1 bulan',
    trainsTeacher: 'Melatih 1 guru dengan metode TaRL',
    otherPrograms: 'Program Lain yang Mungkin Anda Suka',
    principal: 'Kepala Sekolah',
    totalStudents: 'Jumlah Siswa',
    established: 'Tahun Berdiri',
    studentsWillBenefit: 'siswa akan terbantu',
    toGo: 'lagi untuk tercapai',
    cardRaised: 'telah terkumpul',
    cardOf: 'dari',
    cardGoal: 'target',

    // Impact Page
    measuringOurImpact: 'Dampak Kami',
    impactPageDesc:
      'Dalam periode awal pelaksanaan, 216 siswa telah merasakan manfaat pendampingan belajar Accelero—dan jumlah ini terus bertambah.',
    impactByNumbers: 'Jejak Dampak Kami',
    impactStudentsLabel: 'Siswa Telah Terbantu',
    impactStudentsDesc: 'Mendapat pendampingan literasi dan numerasi melalui pendekatan TaRL.',
    impactBooksLabel: 'Buku Telah Didistribusikan',
    impactBooksDesc: 'Untuk menumbuhkan budaya membaca di sekolah tanpa perpustakaan.',
    impactTeachersLabel: 'Guru Telah Dilatih',
    impactTeachersDesc: 'Menguasai pendekatan TaRL untuk meningkatkan kualitas pembelajaran.',
    impactSchoolsLabel: 'Sekolah Telah Terbantu',
    impactSchoolsDesc: 'Menerima dukungan fasilitas belajar dan program after-school.',
    impactRegionLabel: 'Wilayah Telah Dijangkau',
    impactRegionDesc: 'Memulai perubahan berbasis komunitas di Sorong, Papua Barat Daya.',
    impactFundsAmount: 'Rp 285 Juta',
    impactFundsLabel: 'Dana Terkumpul',
    impactFundsDesc: 'Diinvestasikan untuk peningkatan kualitas belajar anak-anak.',

    // Impact Focus Areas
    whereWeCreateImpact: 'Fokus Dampak Kami',
    focusAreasDesc: 'Bidang-bidang pendidikan yang paling membutuhkan intervensi',
    literacyDevelopment: 'Penguatan Literasi',
    literacyDevelopmentDesc: 'Meningkatkan kemampuan membaca siswa melalui pendekatan TaRL',
    literacyStudentsCount: '850+ siswa menunjukkan peningkatan kemampuan membaca',
    numeracySkills: 'Penguatan Numerasi',
    numeracySkillsDesc:
      'Membantu siswa memahami konsep dasar matematika melalui aktivitas belajar interaktif',
    numeracyStudentsCount: '360+ siswa berkembang dalam kemampuan berhitung',
    teacherTrainingImpact: 'Pelatihan Guru',
    teacherTrainingImpactDesc:
      'Membekali guru dengan metode ajar modern untuk dampak jangka panjang',
    teacherTrainingCount: '20+ guru mendapat pelatihan TaRL',

    // Communities Section
    communitiesWeServe: 'Wilayah Dampak',
    communitiesWeServeDesc: 'Menjangkau komunitas sekolah yang paling membutuhkan dukungan',
    papuaWestPapua: 'Papua & Papua Barat',
    studentsCount: 'siswa',
    schoolsCount: 'sekolah',

    // Stories of Change
    storiesOfChange: 'Cerita Perubahan',
    storiesOfChangeDesc: 'Suara langsung dari mereka yang merasakan dampaknya',
    parentTestimonial:
      'Dalam 3 bulan saja, anak saya yang tadinya kesulitan membaca sekarang bisa membaca cerita sendiri. Setiap malam dia membacakan buku untuk adiknya.',
    parentName: '— Ibu Sari, Orang Tua Murid SD Inpres 01 Sorong',
    teacherTestimonial:
      'Pelatihan TaRL benar-benar mengubah cara saya mengajar. Saya jadi lebih memahami kebutuhan tiap siswa, bukan hanya mengejar materi.',
    teacherName: '— Pak Ahmad, Guru SDN 05 Jakarta Selatan',
    principalTestimonial:
      'Ruang baca baru jadi tempat favorit anak-anak. Mereka datang lebih awal hanya untuk membaca. Rasanya seperti mimpi terwujud.',
    principalName: '— Suster Maria, Kepala Sekolah SD Katolik Ende',

    // How We Measure
    howWeMeasureImpact: 'Bagaimana Kami Mengukur Dampak',
    howWeMeasureDesc: 'Setiap program diukur dengan ketat dan transparan',
    baselineAssessments: 'Baseline Assessment',
    baselineAssessmentsDesc: 'Pengukuran awal kemampuan siswa',
    ongoingTracking: 'Pemantauan Berkala',
    ongoingTrackingDesc: 'Evaluasi mingguan & bulanan untuk memantau perkembangan',
    finalAssessment: 'Final Assessment',
    finalAssessmentDesc: 'Penilaian akhir untuk mengukur hasil program',
    transparentReportingImpact: 'Laporan Transparan',
    transparentReportingImpactDesc: 'Setiap donatur menerima update lengkap',

    // Learning Gains
    averageLearningGains: 'Rata-Rata Peningkatan Belajar',
    literacyGain: 'Literasi',
    numeracyGain: 'Numerasi',
    learningBenchmarkDesc: 'Persentase siswa yang mencapai level belajar sesuai usia',

    // CTA
    bePartOfImpact: 'Jadilah Bagian dari Perubahan',
    bePartOfImpactDesc:
      'Setiap donasi menciptakan lingkaran kebaikan bagi masa depan anak-anak Indonesia.',
    makeADonation: 'Donasi Sekarang',
    browsePrograms: 'Lihat Program',

    // Volunteer Form
    volunteerApplication: 'Formulir Pendaftaran Relawan',
    firstName: 'Nama Depan',
    lastName: 'Nama Belakang',
    email: 'Email',
    phone: 'Telepon',
    volunteerCategory: 'Kategori Relawan',
    categoryIndividual: 'Individu',
    categoryUniversity: 'Universitas Mitra',
    preferredRoles: 'Peran yang Diminati',
    selectAtLeastOne: 'Pilih minimal satu:',
    formRoleTeacher: 'Guru (Numerasi / Literasi)',
    formRoleSpecialEd: 'Guru Khusus (ABK)',
    formRoleCurriculum: 'Curriculum Support',
    formRoleFieldOps: 'Operasional Lapangan (Project Manager)',
    formRoleDocumentation: 'Dokumentasi',
    formRoleCommunity: 'Community Support',
    relevantExperience: 'Pengalaman Relevan (Opsional)',
    whyVolunteerQuestion: 'Motivasi Menjadi Relawan',
    submitApplication: 'Kirim Formulir',
    submitting: 'Mengirim...',
    experiencePlaceholder:
      'Ceritakan pengalaman mengajar, kegiatan sosial, keterampilan teknis, atau pengalaman lainnya.',
    motivationPlaceholder: 'Mengapa Anda ingin bergabung dengan Accelero?',
    required: '*',
    whyVolunteer: 'Kenapa Jadi Relawan?',
    makeAnImpact: 'Dampak Nyata',
    makeAnImpactDesc:
      'Terlibat langsung dalam kegiatan belajar-mengajar dan pengembangan sekolah yang memberi manfaat langsung bagi siswa dan guru.',
    learnAndGrow: 'Pengembangan Diri',
    learnAndGrowDesc:
      'Kembangkan keterampilan mengajar, komunikasi, leadership, manajemen kelas, operasional lapangan, dan kerja tim.',
    connect: 'Terhubung Dengan Komunitas',
    connectDesc:
      'Bertemu relawan dari berbagai daerah—individu dan universitas mitra—yang sama-sama peduli pada pendidikan Indonesia.',
    empower: 'Memberdayakan Sekolah',
    empowerDesc:
      'Bantu sekolah meningkatkan kualitas pembelajaran melalui kegiatan akademik dan non-akademik.',
    volunteerCategories: 'Kategori Relawan',
    individualVolunteers: 'Relawan Individu',
    individualVolunteersDesc:
      'Terbuka untuk mahasiswa, profesional muda, atau siapa pun yang ingin berkontribusi di lapangan.',
    universityVolunteers: 'Relawan Universitas Mitra',
    universityVolunteersDesc:
      'Program relawan terstruktur untuk mahasiswa dari kampus yang bekerja sama dengan Accelero.',
    volunteerRoles: 'Peran Relawan',
    academicVolunteers: 'Relawan Akademik',
    roleTeacher: 'Guru (Numerasi, Literasi)',
    roleSpecialEd: 'Guru Khusus (Anak Berkebutuhan Khusus/ABK)',
    roleCurriculum: 'Curriculum Support',
    nonAcademicVolunteers: 'Relawan Non-Akademik',
    roleFieldOps: 'Operasional lapangan (Project Manager)',
    roleDocumentation: 'Dokumentasi (foto & video)',
    roleCommunity: 'Community Support',
    allRolesOnField: 'Semua peran bersifat on-field dan ditempatkan di sekolah mitra Accelero.',

    // Messages
    thankYouVolunteer: 'Terima kasih atas minat Anda! Kami akan menghubungi Anda segera.',
    somethingWentWrong: 'Terjadi kesalahan. Silakan coba lagi.',
    failedToSubmit: 'Gagal mengirim. Silakan coba lagi nanti.',

    // About Page
    aboutAccelero: 'Tentang Accelero',
    aboutHeroTitle: 'Mewujudkan Pendidikan Setara untuk Semua Anak di Indonesia',
    aboutHeroSubtitle:
      'Accelero lahir dari kepedulian terhadap kesenjangan pendidikan yang masih dirasakan anak-anak di berbagai pelosok Indonesia. Banyak dari mereka memiliki potensi besar, namun tertinggal karena tidak mendapatkan fondasi belajar yang kuat sejak dini. Kami hadir untuk menjembatani ketimpangan itu—membangun dasar literasi dan numerasi yang kokoh, agar setiap anak, di mana pun mereka tinggal, memiliki kesempatan yang sama untuk berkembang dan mengejar masa depan yang mereka impikan.',

    ourStory: 'Kisah Kami',
    bornFromExperience: 'Berangkat dari Pengalaman yang Pernah Kami Alami',
    ourStoryText1:
      'Accelero lahir dari perjalanan kami—yang tumbuh di daerah rural dan merasakan langsung ketimpangan pendidikan dengan kota besar. Ketika kami melanjutkan pendidikan di luar daerah, kami menyadari bahwa banyak anak tertinggal bukan karena kurang kemampuan, tetapi karena fondasi belajar mereka tidak pernah benar-benar dikuatkan sejak awal.',
    ourStoryText2:
      'Kesadaran itulah yang mendorong kami membangun Accelero Indonesia: sebuah gerakan untuk memastikan anak-anak di daerah terpencil tidak perlu merasakan ketertinggalan yang sama. Kami percaya bahwa setiap anak, di mana pun mereka lahir, berhak memiliki fondasi literasi dan numerasi yang kuat agar bisa mengejar mimpi mereka dengan percaya diri.',
    dPrizeRecipient: 'Pemenang D-Prize Global Competition 2025',
    dPrizeDesc:
      'Diakui secara internasional atas pendekatan inovatif dalam memperluas solusi pendidikan berbasis bukti di wilayah kurang terlayani.',

    ourMission: 'Misi Kami',
    missionText:
      'Mempercepat kemampuan literasi dan numerasi dasar anak-anak di komunitas paling terpinggirkan di Indonesia melalui pendekatan pengajaran berbasis bukti dan pemberdayaan masyarakat lokal.',
    ourVision: 'Visi Kami',
    visionText:
      'Indonesia di mana setiap anak dapat belajar dan berkembang tanpa dibatasi oleh tempat mereka dilahirkan.',

    whatWeDo: 'Apa yang Kami Lakukan',
    evidenceBasedEducation: 'Pendidikan Berbasis Bukti, Dampak yang Tumbuh dari Komunitas',
    whatWeDoDesc:
      'Kami membawa pendekatan Teaching at the Right Level (TaRL), metode yang telah terbukti secara global—ke sekolah-sekolah di daerah terpencil di Indonesia.',

    afterSchoolPrograms: 'Program Belajar Setelah Sekolah',
    afterSchoolDesc:
      'Mendampingi siswa kelas 3–6 yang masih tertinggal dalam literasi dan numerasi dengan pengajaran sesuai tingkat pemahaman mereka, bukan tingkat kelasnya.',
    universityVolunteer: 'Pelatihan Relawan Mahasiswa',
    universityVolunteerDesc:
      'Berkolaborasi dengan universitas lokal untuk merekrut dan melatih mahasiswa sebagai pengajar yang turut memperkuat komunitas mereka sendiri.',
    literacyRichEnvironments: 'Membangun Lingkungan Kaya Literasi',
    literacyRichDesc:
      'Menghadirkan buku bacaan dan bahan belajar melalui donasi dan kemitraan lokal untuk menciptakan ruang belajar yang menyenangkan.',
    communityPartnerships: 'Kemitraan dengan Komunitas Lokal',
    communityPartnershipsDesc:
      'Bekerja bersama sekolah, pemerintah daerah, dan universitas untuk membangun ekosistem belajar yang inklusif dan berkelanjutan.',
    aboutImpactTitle: 'Jejak Dampak Accelero',
    aboutImpactDesc: 'Perubahan nyata dan dampak yang terus bertumbuh.',
    currentAchievements: 'Capaian Saat Ini',
    achChildren: 'Anak Dibimbing',
    achChildrenDesc:
      'Mendapat pendampingan literasi dan numerasi melalui sesi belajar setelah sekolah.',
    achTeachers: 'Community Teacher Terlatih',
    achTeachersDesc:
      'Relawan mahasiswa dan calon guru yang kami latih dengan metode TaRL dan praktik pengajaran langsung.',
    achSchools: 'Sekolah Mitra',
    achSchoolsDesc:
      'Bekerja langsung bersama sekolah dasar untuk memperkuat fondasi belajar siswa.',
    achUniversities: 'Universitas Mitra',
    achUniversitiesDesc:
      'Kolaborasi dengan kampus lokal untuk mencetak pendidik muda berbasis bukti.',
    achLiteracy: 'Peningkatan Literasi',
    achLiteracyDesc: 'Siswa naik tingkat kemampuan membaca dalam waktu beberapa minggu intervensi.',
    achNumeracy: 'Peningkatan Numerasi',
    achNumeracyDesc:
      'Kemajuan signifikan dalam kemampuan berhitung dasar siswa setelah mengikuti program.',
    goals2026: 'Goals 2026',
    goals2026Desc:
      'Mewujudkan perluasan program untuk menjangkau lebih banyak anak di berbagai daerah seluruh Indonesia.',
    goalChildrenNumber: '5.000+',
    goalChildren: 'Anak Terjangkau',
    goalChildrenDesc: 'Target ekspansi ke 5 provinsi dengan model implementasi yang terukur.',
    goalTeachersNumber: '500+',
    goalTeachers: 'Community Teacher Terlatih',
    goalTeachersDesc:
      'Relawan dan calon guru yang diberdayakan melalui pelatihan berbasis bukti dan pengalaman mengajar langsung.',

    whyDifferent: 'Apa yang Membedakan Kami',
    sustainableCommunityModel: 'Model Berkelanjutan yang Digerakkan oleh Komunitas',
    equitableAccess: 'Akses Pendidikan yang Setara',
    equitableAccessDesc:
      'Setiap anak berhak mendapatkan pendidikan berkualitas—di mana pun mereka tinggal.',
    localTalent: 'Talenta Lokal yang Berkembang',
    localTalentDesc:
      'Kami merekrut dan mengembangkan pendidik dari komunitas tempat kami bekerja. Pendekatan ini memastikan relevansi budaya, kepemilikan lokal, dan keberlanjutan jangka panjang.',
    replicableModel: 'Model yang Dapat Direplikasi',
    replicableModelDesc:
      'Melalui kolaborasi dengan universitas mitra, model program kami dapat diperluas ke berbagai wilayah di Indonesia, menciptakan efek multiplier terhadap dampak pendidikan.',
    sustainableApproach: 'Pendekatan Berkelanjutan',
    sustainableApproachDesc:
      'Kami membangun kapasitas komunitas, bukan ketergantungan. Dampak berlanjut bahkan setelah program selesai.',
    holisticSupport: 'Pendekatan Holistik',
    holisticSupportDesc:
      'Transformasi pendidikan tidak cukup hanya melalui kurikulum. Kami menggabungkan inovasi pembelajaran, budaya literasi, dan dukungan infrastruktur sekolah sebagai satu kesatuan.',

    ourTeam: 'Tim Kami',
    meetTheFounders: 'Bertemu dengan Para Pendiri',
    passionateEducators:
      'Digerakkan oleh pengalaman pribadi dan kepedulian mendalam untuk membuka akses pendidikan yang lebih setara bagi anak-anak Indonesia.',
    catherineRole: 'Founder, Program & Partnerships Director',
    catherineBio:
      'Memimpin jalannya program Accelero di lapangan, termasuk merancang dan menjalankan program belajar setelah sekolah dengan metode Teaching at the Right Level (TaRL). Catherine juga menguatkan kemitraan dengan sekolah dan universitas lokal, memastikan setiap anak mendapatkan pengalaman belajar yang bermakna dan berkualitas.',
    kevinRole: 'Co-Founder & Strategic Program Lead',
    kevinBio:
      'Memimpin strategi pertumbuhan dan pengembangan Accelero ke berbagai wilayah. Kevin fokus pada inovasi program, membangun ekosistem pembelajaran berbasis komunitas, dan merancang strategi agar dampak pendidikan dapat dirasakan lebih luas oleh anak-anak di seluruh Indonesia.',

    joinTheMovement: 'Bergabung dengan Gerakan',
    joinMovementDesc:
      'Apakah Anda mahasiswa, pendidik, donatur, atau mitra organisasi? Mari bergabung dengan Accelero untuk memastikan setiap anak di Indonesia bisa belajar dan berkembang.',
    supportOurMission: 'Dukung Misi Kami',

    // Contact Page
    getInTouch: 'Hubungi Kami',
    contactHeroTitle: 'Sampaikan Pertanyaan atau Masukan Anda',
    contactHeroSubtitle:
      'Punya pertanyaan tentang program kami? Ingin menjadi relawan atau menjajaki kemitraan? Tim kami siap membantu dan dengan senang hati terhubung dengan Anda.',

    emailUs: 'Email',
    emailUsDesc: 'Kirim pertanyaan Anda melalui email, biasanya kami merespons dalam 24 jam.',
    whatsApp: 'WhatsApp',
    whatsAppDesc: 'Untuk pertanyaan cepat, silakan hubungi kami melalui WhatsApp:',
    officeLocation: 'Lokasi',
    officeLocationDesc: 'Jakarta, Indonesia (Operasional Remote)',
    workingRemotely: 'Bekerja secara fleksibel di berbagai wilayah di Indonesia.',

    sendUsMessage: 'Kirim Pesan kepada Kami',
    sendUsMessageDesc: 'Isi formulir berikut, dan kami akan membalas secepatnya.',
    fullName: 'Nama Lengkap',
    fullNamePlaceholder: 'Nama lengkap Anda',
    emailAddress: 'Alamat Email',
    emailPlaceholder: 'email@anda.com',
    phoneNumber: 'Nomor Telepon',
    phonePlaceholder: '+62 xxx xxxx xxxx',
    subject: 'Subjek',

    generalInquiry: 'Pertanyaan Umum',
    volunteerOpportunity: 'Relawan',
    partnership: 'Kemitraan',
    donationQuestion: 'Donasi',
    mediaPress: 'Media/Pers',
    other: 'Lainnya',

    yourMessage: 'Pesan Anda',
    messagePlaceholder: 'Ceritakan lebih lanjut tentang pertanyaan Anda...',
    sending: 'Mengirim...',
    sendMessage: 'Kirim Pesan',

    thankYouContact:
      'Terima kasih telah menghubungi kami! Kami akan menghubungi Anda dalam 24 jam.',
    contactError: 'Terjadi kesalahan. Silakan coba lagi atau email kami langsung.',

    quickConnect: 'Hubungi Lebih Cepat',
    quickConnectDesc: 'Jika ingin respons lebih cepat, Anda bisa langsung:',
    messageOnWhatsApp: 'Chat via WhatsApp',
    sendEmail: 'Kirim Email',

    officeHours: 'Jam Operasional',
    mondayFriday: 'Senin – Jumat',
    hoursWeekday: '09.00 – 18.00 WIB',
    saturday: 'Sabtu',
    hoursSaturday: '10.00 – 16.00 WIB',
    sunday: 'Minggu',
    closed: 'Libur',

    needHelp: 'Butuh Bantuan Cepat?',
    needHelpDesc: 'Kunjungi halaman FAQ untuk jawaban atas pertanyaan yang paling sering diajukan.',
    visitHelpCenter: 'Lihat Pusat Bantuan →',

    faq: 'FAQ',
    frequentlyAskedQuestions: 'Pertanyaan yang Sering Diajukan',

    howCanIVolunteer: 'Bagaimana cara menjadi relawan di Accelero?',
    howCanIVolunteerAnswer:
      'Accelero membuka kesempatan bagi individu maupun mahasiswa melalui program universitas mitra untuk mendukung pendidikan anak-anak di daerah kurang terlayani. Relawan dapat membantu dalam pendampingan akademik maupun dukungan non-akademik, semuanya dilakukan on-field di sekolah mitra. Pendaftaran dapat dilakukan melalui halaman volunteer.',

    howDoIMakeDonation: 'Bagaimana cara berdonasi?',
    howDoIMakeDonationAnswer:
      'Donasi dapat dilakukan melalui halaman donasi resmi kami. Semua kontribusi dilacak secara transparan.',

    canMyOrganizationPartner: 'Bisakah organisasi saya bekerja sama dengan Accelero?',
    canMyOrganizationPartnerAnswer:
      'Tentu. Kami terbuka untuk kemitraan dengan sekolah, universitas, NGO, dan perusahaan. Kirimkan pesan dengan subjek “Kemitraan”.',

    howDoIGetUpdates: 'Bagaimana cara mendapatkan update program?',
    howDoIGetUpdatesAnswer:
      'Anda dapat berlangganan newsletter di halaman donasi, atau mengikuti kanal media sosial kami untuk pembaruan rutin.',

    joinOurMission: 'Bergabung dalam Misi Kami',
    joinOurMissionDesc:
      'Ada banyak cara untuk berkontribusi—melalui donasi, kemitraan, atau menjadi relawan.',
    viewOurPrograms: 'Lihat Program Kami',
    contactDonateCta: 'Beri Donasi',
    volunteerHeroTitle: 'Jadi Relawan Accelero',
    volunteerHeroSubtitle:
      'Bantu wujudkan pendidikan yang lebih setara, di mana setiap anak punya kesempatan belajar, tanpa ada yang tertinggal.',

    // Blog Page
    ourBlog: 'Blog Kami',
    storiesOfImpact: 'Kisah Dampak',
    blogHeroSubtitle:
      'Temukan berita terbaru, kisah dampak, dan pembaruan dari pekerjaan kami di bidang pendidikan dan pengembangan komunitas.',
    searchArticles: 'Cari artikel...',
    allPosts: 'Semua Postingan',
    impactStoriesCategory: 'Kisah Dampak',
    updatesCategory: 'Pembaruan',
    eventsCategory: 'Acara',
    teamNewsCategory: 'Berita Tim',
    clearFilters: 'Hapus Filter',
    showing: 'Menampilkan',
    of: 'dari',
    articles: 'artikel',
    noArticlesFound: 'Artikel Tidak Ditemukan',
    noArticlesMessage:
      'Kami tidak dapat menemukan artikel yang sesuai dengan kriteria Anda. Coba sesuaikan filter atau kata kunci pencarian Anda.',
    stayUpdated: 'Tetap Terkini',
    subscribeNewsletter:
      'Berlangganan newsletter kami untuk menerima kisah dampak terbaru, pembaruan program, dan peluang relawan langsung di kotak masuk Anda.',
    enterYourEmail: 'Masukkan email Anda',
    subscribeBtn: 'Berlangganan',
    minRead: 'menit baca',

    //Donation Page
    makeAnImpactDonate: 'Buat Dampak',
    everyDonationChangesLives: 'Setiap Donasi Mengubah Hidup',
    donatePageDesc:
      'Kontribusi Anda menyediakan buku, pelatihan, dan harapan bagi siswa yang paling membutuhkannya. 100% transparan, 100% berdampak.',
    chooseYourProject: 'Pilih Proyek Anda',
    supportProject: 'Dukung',
    generalFund: 'Dana Umum - Untuk Kebutuhan Paling Mendesak',
    chooseYourAmount: 'Pilih Jumlah Donasi Anda',
    oneTime: 'Sekali',
    monthly: 'Bulanan',
    monthlyInfo:
      'Donasi bulanan membantu kami merencanakan dengan lebih baik dan menciptakan dampak jangka panjang',
    orEnterCustomAmount: 'Atau masukkan jumlah sendiri',
    enterAmount: 'Masukkan jumlah',
    minimumDonation: 'Donasi minimum: Rp 10.000',
    shareYourMotivation: 'Bagikan Motivasi Anda',
    whatInspiredYou: 'Apa yang menginspirasi Anda untuk memberi hari ini?',
    selectMotivation: 'Pilih motivasi Anda...',
    believeInEducationEquity: 'Saya percaya pada kesetaraan pendidikan',
    supportingCommunity: 'Mendukung komunitas saya',
    wantMeasurableImpact: 'Ingin melihat dampak yang terukur',
    investingInFuture: 'Berinvestasi di masa depan anak-anak',
    taxDeductible: 'Donasi dapat dikurangkan pajak',
    messageOfEncouragement: 'Pesan Semangat',
    optional: '(Opsional)',
    sendMessageToStudents: 'Kirim pesan ke siswa dan guru...',
    yourInformation: 'Informasi Anda',
    enterFullName: 'Masukkan nama lengkap Anda',
    yourEmail: 'email@anda.com',
    phoneNumberOptional: 'Nomor Telepon',
    phoneNumberPlaceholder: '+62 xxx xxxx xxxx',
    makeDonationAnonymous: 'Buat donasi saya anonim',
    sendMonthlyUpdates: 'Kirimkan saya pembaruan dampak bulanan',
    proceedToPayment: 'Lanjutkan ke Pembayaran',
    processing: 'Memproses...',
    donationSecure:
      'Donasi Anda aman dan terenkripsi. Kami tidak pernah menyimpan detail pembayaran Anda.',
    donationSummary: 'Ringkasan Donasi',
    donationType: 'Jenis Donasi',
    oneTimeDonation: 'Sekali',
    monthlyRecurring: 'Bulanan Berulang',
    amount: 'Jumlah',
    supporting: 'Mendukung',
    yourImpactSummary: 'Dampak Anda',
    seeHowDonationHelps2: 'Lihat bagaimana donasi Anda membantu:',
    annualTotal: 'Total tahunan',
    whyDonateWithUs: 'Mengapa Donasi dengan Kami?',
    hundredPercentTransparent: '100% Transparan',
    trackDonation: 'Lacak dengan tepat kemana donasi Anda pergi',
    taxDeductibleReceipt: 'Dapat Dikurangkan Pajak',
    receiveOfficialReceipt: 'Terima tanda terima donasi resmi',
    securePayment: 'Pembayaran Aman',
    bankLevelEncryption: 'Enkripsi tingkat bank',
    frequentlyAskedQuestionsDonate: 'Pertanyaan yang Sering Diajukan',
    howIsMyDonationUsed: 'Bagaimana donasi saya digunakan?',
    howIsMyDonationUsedAnswer:
      '100% dari donasi Anda langsung digunakan untuk biaya program - buku, pelatihan guru, dan materi siswa. Biaya administrasi ditanggung secara terpisah.',
    willIReceiveReceipt: 'Apakah saya akan menerima tanda terima?',
    willIReceiveReceiptAnswer:
      'Ya! Anda akan menerima tanda terima email segera setelah donasi, yang berlaku untuk tujuan pengurangan pajak.',
    canICancelMonthly: 'Bisakah saya membatalkan donasi bulanan?',
    canICancelMonthlyAnswer:
      'Tentu saja. Anda dapat membatalkan atau mengubah donasi bulanan Anda kapan saja dari dasbor donatur Anda.',
    howTrackImpact: 'Bagaimana cara melacak dampak?',
    howTrackImpactAnswer:
      'Kami mengirimkan pembaruan bulanan dengan foto, cerita, dan hasil terukur dari program yang Anda dukung.',
    studentsFunded: 'siswa didanai',
    canTrainTeachers: 'dapat melatih',
    teachers: 'guru dalam metodologi TaRL',
    canSupport: 'dapat mendukung',
    studentsForMonth: 'siswa selama satu bulan',
    canProvide: 'dapat menyediakan',
    readingBooks: 'buku bacaan',
    willMakeRealDifference: 'akan membuat perbedaan nyata dalam kehidupan siswa',
    minimumDonationAlert: 'Donasi minimum adalah Rp 10.000',
    fillNameEmail: 'Harap isi nama dan email Anda',
    // QRIS Payment Modal
    scanQRToDonate: 'Pindai Kode QR untuk Donasi',
    qrisInstructions:
      'Buka aplikasi mobile banking atau e-wallet Anda dan pindai kode QR ini untuk menyelesaikan donasi Anda.',
    donationAmount: 'Jumlah Donasi',
    scanWithAnyApp:
      'Pindai dengan aplikasi perbankan atau e-wallet (GoPay, OVO, Dana, ShopeePay, dll.)',
    afterPayment:
      'Setelah menyelesaikan pembayaran, silakan masukkan nomor rekening atau Nomor Ponsel pengirim Anda:',
    senderAccountNumber: 'Nomor Rekening Anda atau Nomor ponsel yang digunakan',
    enterSenderAccount: 'Masukkan nomor rekening bank atau nomor ponsel Anda',
    accountNumberPlaceholder: 'contoh: 1234567890',
    confirmDonation: 'Konfirmasi Donasi',
    closeModal: 'Tutup',
    accountNumberRequired: 'Silakan masukkan nomor rekening Anda',
    thankYouForDonation:
      'Terima kasih atas donasi Anda! Kami akan memverifikasi pembayaran Anda dan mengirimkan email konfirmasi segera.',
    orTransferDirectly: 'Atau transfer langsung ke rekening kami:',
    bankName: 'Bank',
    accountName: 'Nama Rekening',
    copyAccountNumber: 'Salin Nomor Rekening',
    accountCopied: 'Nomor rekening tersalin!',
    // Footer
    footerMission:
      'Memberdayakan komunitas kurang terlayani di Indonesia melalui pendidikan berkualitas. Setiap anak berhak untuk belajar.',
    aboutUs: 'Tentang Kami',
    impactStories: 'Kisah Dampak',
    programs: 'Program',
    resources: 'Sumber Daya',
    helpCenter: 'Pusat Bantuan',
    newsletterDescription:
      'Dapatkan update bulanan tentang program kami dan lihat dampak dukungan Anda',
    subscribe: 'Berlangganan',
    allRightsReserved: 'Hak cipta dilindungi',
    secureDonations: 'Donasi Aman',
    verifiedNonprofit: 'Nirlaba Terverifikasi',
    transparency: 'Transparansi',
  },
}

export type TranslationKey = keyof typeof translations.en

// Compile-time check: every English key must also exist in Indonesian,
// otherwise the Indonesian site would show the raw key name.
const indonesianHasEveryKey: Record<TranslationKey, string> = translations.id
void indonesianHasEveryKey

export function getTranslation(language: Language) {
  return (key: TranslationKey): string => {
    return translations[language][key] || translations.en[key] || key
  }
}
